import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { getFirestore, type Firestore } from "firebase/firestore";
import {
  initializeAppCheck,
  getToken,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from "firebase/app-check";

// DRIFT WARNING: duplicated from packages/admin-ui/src/firebase-env.ts's
// DEV_FIREBASE_DEFAULTS because this app doesn't depend on @workspace/admin-ui.
// Keep these values in lockstep with that file (and with all four apps'
// .env.local) whenever the dev Firebase web app config changes.
const DEV_FIREBASE_DEFAULTS: Record<string, string> = {
  VITE_FIREBASE_API_KEY: "AIzaSyD8VQ_YnnlaozG9VjB0py2-2CPn4ayDQa0",
  VITE_FIREBASE_AUTH_DOMAIN: "katanaai-pharma-dev.firebaseapp.com",
  VITE_FIREBASE_PROJECT_ID: "katanaai-pharma-dev",
  VITE_FIREBASE_STORAGE_BUCKET: "katanaai-pharma-dev.firebasestorage.app",
  VITE_FIREBASE_MESSAGING_SENDER_ID: "583647062253",
  VITE_FIREBASE_APP_ID: "1:583647062253:web:72e743890526f1094e04e4",
};

// Falls back to the dev Firebase project when a var is missing, so the
// default environment is always dev — never a silent/undefined state.
function readEnv(name: string): string {
  const v = (import.meta.env as Record<string, string | undefined>)[name];
  if (v && v.length > 0) return v;
  const fallback = DEV_FIREBASE_DEFAULTS[name];
  if (fallback) {
    console.warn(
      `[firebase] ${name} not set in artifacts/booking/.env.local or deployment env — defaulting to the dev Firebase project.`,
    );
    return fallback;
  }
  throw new Error(
    `Missing required env var ${name}. Set it in artifacts/booking/.env.local or in deployment env.`,
  );
}

const firebaseConfig = {
  apiKey: readEnv("VITE_FIREBASE_API_KEY"),
  authDomain: readEnv("VITE_FIREBASE_AUTH_DOMAIN"),
  projectId: readEnv("VITE_FIREBASE_PROJECT_ID"),
  storageBucket: readEnv("VITE_FIREBASE_STORAGE_BUCKET"),
  messagingSenderId: readEnv("VITE_FIREBASE_MESSAGING_SENDER_ID"),
  appId: readEnv("VITE_FIREBASE_APP_ID"),
};

// Single source of truth for the Cloud Functions base URL, derived from the
// same resolved project id as Firestore above. Do NOT hardcode this base
// anywhere else (api.ts, BookingPage.tsx, etc.) — always import it from
// here, or dev will silently call prod functions against dev Firestore data
// (and vice versa).
export const FUNCTIONS_BASE_URL = `https://europe-west1-${firebaseConfig.projectId}.cloudfunctions.net`;

// Structurally the URL above is always derived from firebaseConfig.projectId,
// so this can't actually diverge — but assert it explicitly so a future
// refactor that reintroduces a separate literal fails loudly at startup
// instead of silently hitting the wrong backend.
if (!FUNCTIONS_BASE_URL.includes(`-${firebaseConfig.projectId}.`)) {
  throw new Error(
    `[firebase] FUNCTIONS_BASE_URL project (${FUNCTIONS_BASE_URL}) does not match the resolved Firestore project (${firebaseConfig.projectId}). Refusing to start with a mismatched backend.`,
  );
}

let app: FirebaseApp;
let db: Firestore;
let appCheck: AppCheck | null = null;
let appCheckInitialized = false;

export function getFirebaseApp(): FirebaseApp {
  if (!app) {
    app = getApps().length > 0 ? getApps()[0]! : initializeApp(firebaseConfig);
  }
  return app;
}

/**
 * Initialize Firebase App Check once, gated on a reCAPTCHA site key in env.
 * Absent key = App Check off (client behaves exactly as before), so this is
 * safe to ship before the key is provisioned in the Firebase console.
 */
function getAppCheck(): AppCheck | null {
  if (appCheckInitialized) return appCheck;
  appCheckInitialized = true;
  const env = import.meta.env as Record<string, string | undefined>;
  const siteKey = env.VITE_APPCHECK_RECAPTCHA_SITE_KEY;
  if (!siteKey) return null;
  const debugToken = env.VITE_APPCHECK_DEBUG_TOKEN;
  if (debugToken && typeof self !== "undefined") {
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      debugToken;
  }
  appCheck = initializeAppCheck(getFirebaseApp(), {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
  return appCheck;
}

/**
 * Returns the `X-Firebase-AppCheck` header value for the booking onRequest
 * endpoints (plain `fetch`, so the SDK does not attach the token itself).
 * Returns `{}` when App Check is not configured or token mint fails — the
 * server is in monitor mode by default, so a missing header is non-fatal.
 */
export async function appCheckHeader(): Promise<Record<string, string>> {
  const ac = getAppCheck();
  if (!ac) return {};
  try {
    const { token } = await getToken(ac, /* forceRefresh */ false);
    return token ? { "X-Firebase-AppCheck": token } : {};
  } catch {
    return {};
  }
}

export function getDb(): Firestore {
  if (!db) {
    db = getFirestore(getFirebaseApp());
  }
  return db;
}
